import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, Tooltip } from "recharts";
import {
  Truck, MagnifyingGlass, CaretDown, CaretRight, Package as PackageIcon,
  CheckCircle, Clock, XCircle, ArrowUUpLeft, ArrowsCounterClockwise, Phone, Envelope,
} from "@phosphor-icons/react";

// Visual treatment per package status — kept in one place so the legend, the
// chip-strip per driver, and the row badges stay consistent.
const STATUS_STYLES = {
  pending: { label: "Pending", color: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
  in_transit: { label: "In transit", color: "bg-blue-50 text-blue-700 border-blue-200", Icon: Truck },
  ready_for_pickup: { label: "Ready for pickup", color: "bg-indigo-50 text-indigo-700 border-indigo-200", Icon: PackageIcon },
  delivered: { label: "Delivered", color: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "bg-zinc-100 text-zinc-600 border-zinc-200", Icon: XCircle },
  awaiting_redelivery: { label: "Redelivery", color: "bg-orange-50 text-orange-700 border-orange-200", Icon: ArrowsCounterClockwise },
  return_to_sender: { label: "Return to sender", color: "bg-red-50 text-red-700 border-red-200", Icon: ArrowUUpLeft },
};
const STATUS_ORDER = ["delivered", "in_transit", "ready_for_pickup", "pending", "awaiting_redelivery", "return_to_sender", "cancelled"];

export default function DriverAssignments() {
  const { user } = useAuth();
  const isPlatform = user.role === "super_admin" || user.role === "warehouse_super";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openIds, setOpenIds] = useState(() => new Set());

  const load = async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/driver-assignments", { params: q ? { q } : {} });
      setData(data);
      // Auto-open the first driver on first load so the UI never feels empty.
      if (!q && data.items?.length && openIds.size === 0) {
        setOpenIds(new Set([data.items[0].driver_id]));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load assignments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => { e.preventDefault(); load(search.trim()); };

  const toggle = (did) => {
    setOpenIds((s) => {
      const n = new Set(s);
      if (n.has(did)) n.delete(did); else n.add(did);
      return n;
    });
  };

  const expandAll = () => setOpenIds(new Set((data?.items || []).map((g) => g.driver_id)));
  const collapseAll = () => setOpenIds(new Set());

  const summary = useMemo(() => {
    if (!data?.items) return null;
    const agg = {};
    let totalAssigned = 0;
    for (const g of data.items) {
      totalAssigned += g.total;
      for (const [s, n] of Object.entries(g.status_counts || {})) agg[s] = (agg[s] || 0) + n;
    }
    return { totalAssigned, agg };
  }, [data]);

  return (
    <div className="p-6" data-testid="driver-assignments-page">
      <Toaster richColors position="top-right" />
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">Operations</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Driver assignments
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Every package that has been assigned to a driver, grouped by driver and shown across every status — pending, in transit, delivered, picked up, returned, cancelled.
          </p>
        </div>
        {data && (
          <div className="text-right text-xs text-zinc-500">
            <div><b className="text-zinc-900 text-base">{data.total_drivers}</b> driver{data.total_drivers === 1 ? "" : "s"}</div>
            <div><b className="text-zinc-900 text-base">{data.total_packages}</b> assigned package{data.total_packages === 1 ? "" : "s"}</div>
          </div>
        )}
      </div>

      <form onSubmit={onSearch} className="mb-4 flex gap-2 max-w-md">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Search tracking #, recipient, phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="driver-assignments-search"
          />
        </div>
        <Button type="submit" variant="outline">Search</Button>
        {search && <Button type="button" variant="ghost" onClick={() => { setSearch(""); load(""); }}>Clear</Button>}
      </form>

      {/* Aggregate status chip strip — shows total counts across all drivers in current scope */}
      {summary && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="driver-assignments-summary">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Status across all drivers</span>
          {STATUS_ORDER.filter((s) => summary.agg[s]).map((s) => {
            const st = STATUS_STYLES[s] || { label: s, color: "bg-zinc-50 text-zinc-700 border-zinc-200", Icon: PackageIcon };
            const SIcon = st.Icon;
            return (
              <span key={s} className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider border rounded-full px-2 py-0.5 ${st.color}`}>
                <SIcon size={10} weight="fill" /> {st.label}: {summary.agg[s]}
              </span>
            );
          })}
        </div>
      )}

      {(data?.items || []).length > 1 && (
        <div className="mb-3 flex items-center gap-2 text-xs">
          <button type="button" onClick={expandAll} className="text-zinc-500 hover:text-zinc-900 underline-offset-2 hover:underline" data-testid="expand-all-btn">Expand all</button>
          <span className="text-zinc-300">·</span>
          <button type="button" onClick={collapseAll} className="text-zinc-500 hover:text-zinc-900 underline-offset-2 hover:underline" data-testid="collapse-all-btn">Collapse all</button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : !data?.items?.length ? (
        <div className="border border-dashed border-zinc-200 rounded-md p-10 text-center" data-testid="driver-assignments-empty">
          <div className="w-12 h-12 mx-auto rounded-md bg-zinc-100 text-zinc-500 flex items-center justify-center mb-3">
            <Truck size={22} weight="bold" />
          </div>
          <div className="font-medium text-zinc-700">No driver assignments yet</div>
          <div className="text-sm text-zinc-500 mt-1">As soon as a dispatcher assigns a package to a driver, it&apos;ll show here.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="driver-assignments-list">
          {data.items.map((g) => {
            const open = openIds.has(g.driver_id);
            return (
              <DriverGroup
                key={g.driver_id}
                group={g}
                open={open}
                onToggle={() => toggle(g.driver_id)}
                isPlatform={isPlatform}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function DriverGroup({ group, open, onToggle, isPlatform }) {
  return (
    <div className="border border-zinc-200 rounded-md bg-white overflow-hidden" data-testid={`driver-group-${group.driver_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 text-left"
        data-testid={`driver-group-toggle-${group.driver_id}`}
      >
        <div className="shrink-0 text-zinc-400">
          {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        </div>
        {/* Avatar */}
        {group.driver_selfie_path ? (
          <img
            src={`/api/files/${group.driver_selfie_path}`}
            alt={group.driver_name}
            className="w-9 h-9 rounded-full object-cover border border-zinc-100"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-zinc-900 text-white flex items-center justify-center text-sm font-bold shrink-0">
            {(group.driver_name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-zinc-900 truncate">{group.driver_name}</span>
            {group.is_manual && (
              <span className="text-[10px] uppercase tracking-wider font-semibold bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5">Manual</span>
            )}
            {group.driver_is_zip_express && (
              <span className="text-[10px] uppercase tracking-wider font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-1.5 py-0.5">ZipExpress</span>
            )}
            {group.is_registered && !group.driver_is_active && (
              <span className="text-[10px] uppercase tracking-wider font-semibold bg-zinc-100 text-zinc-500 border border-zinc-200 rounded-full px-1.5 py-0.5">Inactive</span>
            )}
            {isPlatform && group.driver_company_name && (
              <span className="text-xs text-zinc-500">· {group.driver_company_name}</span>
            )}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-3">
            {group.driver_phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {group.driver_phone}</span>}
            {group.driver_email && <span className="inline-flex items-center gap-1 truncate"><Envelope size={10} /> {group.driver_email}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0">
          {STATUS_ORDER.filter((s) => group.status_counts[s]).map((s) => {
            const st = STATUS_STYLES[s] || { label: s, color: "bg-zinc-50 text-zinc-700 border-zinc-200", Icon: PackageIcon };
            const SIcon = st.Icon;
            return (
              <span key={s} className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider border rounded px-1.5 py-0.5 ${st.color}`}>
                <SIcon size={9} weight="fill" /> {st.label} {group.status_counts[s]}
              </span>
            );
          })}
          <span className="text-xs font-semibold text-zinc-900 ml-1">{group.total}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-zinc-100 bg-zinc-50/40 overflow-x-auto" data-testid={`driver-group-packages-${group.driver_id}`}>
          <DriverPerformanceBar perf={group.performance} />
          <table className="w-full text-sm">
            <thead className="text-zinc-500 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Tracking</th>
                <th className="text-left px-4 py-2 font-semibold">Recipient</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">Updated</th>
                {isPlatform && <th className="text-left px-4 py-2 font-semibold">Company</th>}
              </tr>
            </thead>
            <tbody>
              {group.packages.map((p) => {
                const st = STATUS_STYLES[p.status] || { label: p.status, color: "bg-zinc-50 text-zinc-700 border-zinc-200", Icon: PackageIcon };
                const SIcon = st.Icon;
                return (
                  <tr key={p.id} className="border-t border-zinc-100 hover:bg-white">
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <Link
                        to={`/packages/${p.id}`}
                        className="font-mono text-zinc-900 hover:text-blue-600 hover:underline"
                        data-testid={`assignment-track-${p.tracking_number}`}
                      >
                        {p.tracking_number}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-zinc-800 truncate max-w-[260px]">{p.recipient_name || "—"}</div>
                      {p.recipient_phone && <div className="text-xs text-zinc-400">{p.recipient_phone}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider border rounded-full px-1.5 py-0.5 ${st.color}`}>
                        <SIcon size={9} weight="fill" /> {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-zinc-500 whitespace-nowrap">
                      {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}
                    </td>
                    {isPlatform && (
                      <td className="px-4 py-2.5 text-xs text-zinc-600">{p.company_name || "—"}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Per-driver performance strip rendered just above the packages table when a
 * driver group is expanded. Shows three KPIs (success rate, avg delivery time,
 * delivered sample size) and a 30-day sparkline of delivered vs cancelled jobs.
 */
function DriverPerformanceBar({ perf }) {
  if (!perf) return null;
  const series = perf.series_30d || [];
  const hasActivity = series.some((d) => d.delivered || d.cancelled);
  const fmtHrs = (h) => {
    if (h == null) return "—";
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };
  return (
    <div
      className="border-b border-zinc-100 bg-white px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-4"
      data-testid="driver-performance-bar"
    >
      <div className="flex items-stretch gap-3 flex-wrap">
        <KPI
          label="Success rate"
          value={perf.success_rate_pct == null ? "—" : `${perf.success_rate_pct}%`}
          sub={perf.success_denominator > 0
            ? `${perf.success_numerator}/${perf.success_denominator} completed`
            : "no completed jobs yet"}
          testId="driver-perf-success"
        />
        <KPI
          label="Avg delivery time"
          value={fmtHrs(perf.avg_delivery_hours)}
          sub={perf.delivered_sample > 0
            ? `over ${perf.delivered_sample} delivery${perf.delivered_sample === 1 ? "" : "ies"}`
            : "no deliveries yet"}
          testId="driver-perf-avg"
        />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">
          Last 30 days
        </div>
        {hasActivity ? (
          <div className="h-[44px]" data-testid="driver-perf-sparkline">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                <defs>
                  <linearGradient id={`spark-del-${perf.success_numerator}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <Tooltip
                  cursor={{ stroke: "#10b981", strokeWidth: 1, strokeDasharray: "2 2" }}
                  contentStyle={{ fontSize: 11, padding: "4px 8px" }}
                  formatter={(v, n) => [v, n === "delivered" ? "Delivered" : "Cancelled / RTS"]}
                  labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                />
                <Area
                  type="monotone"
                  dataKey="cancelled"
                  stroke="#9CA3AF"
                  strokeWidth={1}
                  fill="none"
                  dot={false}
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="delivered"
                  stroke="#10b981"
                  strokeWidth={1.75}
                  fill={`url(#spark-del-${perf.success_numerator})`}
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-[11px] text-zinc-400 italic h-[44px] flex items-center">
            No completed jobs in the last 30 days
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, sub, testId }) {
  return (
    <div className="border border-zinc-100 rounded-md px-3 py-2 bg-zinc-50/60" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Work Sans" }}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

